import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Card, 
  Table, 
  Tag, 
  Space, 
  Statistic, 
  Row, 
  Col,
  message,
  Button,
  Select,
  DatePicker
} from 'antd';
import { 
  PhoneOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs from 'dayjs';
import { authService } from '../services/auth';

const { Content } = Layout;
const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface OtpRequest {
  id: string;
  phone: string;
  otp_code: string;
  purpose: 'registration' | 'login' | 'verification';
  status: 'pending' | 'verified' | 'expired' | 'failed';
  attempts: number;
  created_at: string;
  expires_at: string;
  verified_at?: string;
}

const OTPPage: React.FC = () => {
  const [otpRequests, setOtpRequests] = useState<OtpRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    verified: 0,
    expired: 0,
    failed: 0
  });

  // OTP isteklerini getir
  const fetchOtpRequests = async () => {
    setLoading(true);
    try {
      console.log('📱 Fetching OTP requests...');
      const api = authService.getApiInstance();
      const response = await api.get('/api/data/otp-requests', {
        timeout: 30000 // 30 seconds
      });

      if (response.data.success) {
        setOtpRequests(response.data.data.otpRequests);
        setStats(response.data.data.stats);
        console.log('✅ OTP requests loaded:', response.data.data.otpRequests.length);
      }
    } catch (error: any) {
      console.error('❌ OTP requests fetch error:', error);
      message.error('OTP istekleri yüklenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOtpRequests();
    // Her 30 saniyede bir otomatik yenile
    const interval = setInterval(fetchOtpRequests, 30000);
    return () => clearInterval(interval);
  }, []);

  // Durum rengi
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'green';
      case 'pending': return 'blue';
      case 'expired': return 'orange';
      case 'failed': return 'red';
      default: return 'default';
    }
  };

  // Durum ikonu
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircleOutlined />;
      case 'pending': return <ClockCircleOutlined />;
      case 'expired': return <ExclamationCircleOutlined />;
      case 'failed': return <CloseCircleOutlined />;
      default: return null;
    }
  };

  // Amaç çevirisi
  const getPurposeText = (purpose: string) => {
    switch (purpose) {
      case 'registration': return 'Kayıt';
      case 'login': return 'Giriş';
      case 'verification': return 'Doğrulama';
      default: return purpose;
    }
  };

  // Süre kalan hesaplama
  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    
    if (diff <= 0) return 'Süresi dolmuş';
    
    const minutes = Math.floor(diff / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const columns: ColumnsType<OtpRequest> = [
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => (
        <Space>
          <PhoneOutlined />
          <span style={{ fontFamily: 'monospace' }}>{phone}</span>
        </Space>
      ),
    },
    {
      title: 'OTP Kodu',
      dataIndex: 'otp_code',
      key: 'otp_code',
      render: (code) => (
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '16px', 
          fontWeight: 'bold',
          color: '#1890ff'
        }}>
          {code}
        </span>
      ),
    },
    {
      title: 'Amaç',
      dataIndex: 'purpose',
      key: 'purpose',
      render: (purpose) => (
        <Tag color="blue">{getPurposeText(purpose)}</Tag>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag 
          color={getStatusColor(status)}
          icon={getStatusIcon(status)}
        >
          {status === 'verified' && 'Doğrulandı'}
          {status === 'pending' && 'Beklemede'}
          {status === 'expired' && 'Süresi Dolmuş'}
          {status === 'failed' && 'Başarısız'}
        </Tag>
      ),
    },
    {
      title: 'Deneme',
      dataIndex: 'attempts',
      key: 'attempts',
      render: (attempts) => (
        <Tag color={attempts > 3 ? 'red' : attempts > 1 ? 'orange' : 'green'}>
          {attempts}/5
        </Tag>
      ),
    },
    {
      title: 'Oluşturulma',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <div>
          <div>{new Date(date).toLocaleDateString('tr-TR')}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {new Date(date).toLocaleTimeString('tr-TR')}
          </div>
        </div>
      ),
    },
    {
      title: 'Kalan Süre',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (expiresAt, record) => {
        if (record.status !== 'pending') {
          return record.status === 'verified' ? (
            <Tag color="green">Doğrulandı</Tag>
          ) : (
            <Tag color="red">Sona erdi</Tag>
          );
        }
        
        return (
          <span style={{ 
            fontFamily: 'monospace',
            color: new Date(expiresAt) <= new Date() ? '#ff4d4f' : '#1890ff'
          }}>
            {getTimeRemaining(expiresAt)}
          </span>
        );
      },
    },
    {
      title: 'Doğrulama',
      dataIndex: 'verified_at',
      key: 'verified_at',
      render: (verifiedAt) => verifiedAt ? (
        <div>
          <div>{new Date(verifiedAt).toLocaleDateString('tr-TR')}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {new Date(verifiedAt).toLocaleTimeString('tr-TR')}
          </div>
        </div>
      ) : (
        <span style={{ color: '#999' }}>-</span>
      ),
    },
  ];

  return (
    <Content style={{ margin: '24px 16px', padding: 24, minHeight: 280 }}>
      <Title level={2}>🔐 OTP İstekleri</Title>
      
      {/* İstatistikler */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Beklemede"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Doğrulandı"
              value={stats.verified}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Süresi Dolmuş"
              value={stats.expired}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Başarısız"
              value={stats.failed}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Kontroller */}
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Select defaultValue="all" style={{ width: 120 }}>
              <Option value="all">Tümü</Option>
              <Option value="pending">Beklemede</Option>
              <Option value="verified">Doğrulandı</Option>
              <Option value="expired">Süresi Dolmuş</Option>
              <Option value="failed">Başarısız</Option>
            </Select>
            <RangePicker 
              placeholder={['Başlangıç', 'Bitiş']}
              format="DD/MM/YYYY"
            />
          </Space>
          <Button 
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchOtpRequests}
          >
            Yenile
          </Button>
        </Space>
      </Card>

      {/* OTP İstekleri Tablosu */}
      <Card>
        <Table
          columns={columns}
          dataSource={otpRequests}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} / ${total} OTP isteği`,
          }}
          rowClassName={(record) => {
            if (record.status === 'pending' && new Date(record.expires_at) <= new Date()) {
              return 'expired-row';
            }
            return '';
          }}
        />
      </Card>

      <style>{`
        .expired-row {
          background-color: #fff2e8 !important;
        }
        .expired-row:hover {
          background-color: #ffe7ba !important;
        }
      `}</style>
    </Content>
  );
};

export default OTPPage;